import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { RubricTable } from "./RubricTable";

// How this lesson's work is scored — the traits, the scale, and what each level says.
//
// The lesson page described what the work IS and said nothing about how it would be scored, which
// is the half a teacher plans from. A descriptor is what tells a student what the next level asks
// for, and what a teacher checks a score against when they disagree with one.
//
// Fetched on its own rather than carried in the plan: descriptors are prose for every level of
// every trait, and the plan is the NAV — it is read on every screen.
//
// WHAT IT REFUSES TO TIDY:
//  * A trait with no published wording is shown and marked. The scorer skips it, and a page that
//    dropped it would show a rubric with a hole in it nobody can see.
//  * A stacked descriptor stays a list of clauses. The linter flags those because two raters can
//    score the same paper on different clauses; running them together as a sentence hides it.
//  * A level with no descriptor shows as a level with no descriptor.

interface Level {
  level: number;
  descriptor: string | string[] | null;
  stacked: boolean;
}

interface Trait {
  trait_id: string;
  label: string | null;
  standard: string | null;
  kind: string | null;
  levels: Level[];
  /** "2.1": the trait's wording read, the newest published minor of the major its rubric holds. */
  version: string | null;
  published: boolean;
}

interface RubricVersion {
  rubric_version_id: string;
  rubric_name: string | null;
  version: number;
  publisher: string | null;
  rubric_status: string | null;
  traits: Trait[];
}

interface View {
  available: boolean;
  task_id: string;
  /** Whether this lesson's scores count toward measurement: the lesson's own. */
  counts: boolean;
  rubrics: RubricVersion[];
}

// ASKED BY LESSON, and read the way the scorer reads it: the lesson's newest published version, or
// its own row, names the rubric versions its work is scored on. `taskId` is still sent, because a
// lesson scored on no rubric falls back to every rubric its task is scored on.
export function Rubric({ taskId, resourceId }: { taskId: string; resourceId?: string }) {
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setView(null); setError(null);
    const by = resourceId ? `resource_id=${encodeURIComponent(resourceId)}&` : "";
    api.get<View>(`/rubric?${by}task_id=${encodeURIComponent(taskId)}`)
      .then((v) => { if (live) setView(v); })
      .catch((e: unknown) => {
        // The server's sentence. A refused read is not an empty rubric, and saying "none" here
        // would be the screen reporting success while not doing the job.
        if (live) setError(e instanceof ApiError ? e.message : "could not be read");
      });
    return () => { live = false; };
  }, [taskId, resourceId]);

  if (error) return <section className="pv-section"><p className="inc-error">{error}</p></section>;
  if (!view) return null;
  if (view.available === false) {
    return (
      <section className="pv-section">
        <h3>How this is scored</h3>
        <p className="pv-quiet">The rubric could not be read yet.</p>
      </section>
    );
  }
  if (view.rubrics.length === 0) {
    return (
      <section className="pv-section">
        <h3>How this is scored</h3>
        <p className="pv-quiet">
          Nothing is scored at this lesson. Work collected here is kept, and no rubric
          is applied to it.
        </p>
      </section>
    );
  }

  return (
    <section className="pv-section rb">
      <h3>How this is scored</h3>
      {/* Whether this lesson's scores count toward the record, or are practice. The lesson's own,
          set by whoever wrote it — nobody sets it on a folder. */}
      <p className="rb-head">
        <span className={view.counts ? "rb-counts" : "rb-practice"}>
          {view.counts ? "Its scores count toward the record." : "Practice: its scores don't count toward the record."}
        </span>
      </p>
      {view.rubrics.map((r) => (
        <div key={r.rubric_version_id} className="rb-occasion">
          <p className="rb-head">
            <b>{r.rubric_name ?? "Rubric"}</b>
            <span className="rb-rubric">version {r.version}</span>
            {/* A rubric version nobody has published is one nothing should be scored against,
                and this is where a teacher would notice. */}
            {r.rubric_status && r.rubric_status !== "published" && (
              <span className="rb-draft">{r.rubric_status}</span>
            )}
          </p>
          {/* THE SAME TABLE the student's work is read through. A teacher who has read the
              rubric here recognises the grid again with one cell filled, and a score becomes a
              position in a rubric rather than a number with prose under it. */}
          <RubricTable rows={r.traits.map((t) => ({
            trait_id: t.trait_id, label: t.label, standard: t.standard,
            levels: t.levels, published: t.published,
          }))} />
        </div>
      ))}
    </section>
  );
}
